<script setup lang="ts">
  import { nextTick, onMounted, ref, watch } from 'vue'
  import Quill from 'quill'
  import 'quill/dist/quill.snow.css'
  import TurndownService from 'turndown'

  const turndownService = new TurndownService()

  const emit = defineEmits([
    'update:modelValue',
    'isEmptyEditor',
    'pureTextForSuggestions',
    'pastedImage',
    'validationError',
  ])

  const props = defineProps({
    modelValue: {
      type: String,
    },
    attachmentsCount: {
      type: Number,
      default: 0,
    },
    /** Greyed prompt shown while the editor is empty. Quill renders it on the blank body. */
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * Marks the editable body invalid. Quill owns the DOM, so the flag is written onto its
     * root as `aria-invalid` — the same attribute BfTextarea paints its invalid box from.
     */
    invalid: {
      type: Boolean,
      default: false,
    },
    /** Id of the message that explains the invalid state, pointed at while `invalid` holds. */
    describedBy: {
      type: String,
      default: '',
    },
  })

  let tabCount = 0
  let isImagePasting = false

  let quillInstance: Quill | null = null

  const editor = ref<HTMLDivElement | null>(null)
  const maxFilesError = ref(false)
  const invalidFileTypeError = ref(false)
  const MAX_FILES_LIMIT = 10
  const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/gif', 'image/png', 'text/plain', 'application/pdf']
  const ERROR_AUTO_HIDE_TIMEOUT = 7000 // 7 seconds
  let errorTimeoutId: ReturnType<typeof setTimeout> | null = null

  watch(
    () => props.attachmentsCount,
    newCount => {
      if (newCount < MAX_FILES_LIMIT) {
        maxFilesError.value = false
      }
    },
  )

  /** Quill builds its own body, so the ARIA state is written after the instance exists. */
  const applyValidationState = () => {
    const body = quillInstance?.root
    if (!body) return

    if (props.invalid) {
      body.setAttribute('aria-invalid', 'true')
    } else {
      body.removeAttribute('aria-invalid')
    }

    if (props.invalid && props.describedBy) {
      body.setAttribute('aria-describedby', props.describedBy)
    } else {
      body.removeAttribute('aria-describedby')
    }
  }

  watch(() => [props.invalid, props.describedBy], applyValidationState)

  const validateFileType = (type: string): boolean => {
    return ALLOWED_FILE_TYPES.includes(type)
  }

  const clearValidationError = () => {
    invalidFileTypeError.value = false
    emit('validationError', false)
    if (errorTimeoutId) {
      clearTimeout(errorTimeoutId)
      errorTimeoutId = null
    }
  }

  const setValidationErrorWithTimeout = () => {
    invalidFileTypeError.value = true
    emit('validationError', true)

    // Clear any existing timeout
    if (errorTimeoutId) {
      clearTimeout(errorTimeoutId)
    }

    // Set new timeout to auto-hide error
    errorTimeoutId = setTimeout(() => {
      clearValidationError()
    }, ERROR_AUTO_HIDE_TIMEOUT)
  }

  const removeImagesFromEditor = () => {
    if (quillInstance) {
      const delta = quillInstance.getContents()
      const newOps = delta.ops.filter((op: any) => !op.insert?.image)

      if (newOps.length !== delta.ops.length) {
        quillInstance.setContents(newOps)
      }
    }
  }

  onMounted(async () => {
    if (editor.value) {
      quillInstance = new Quill(editor.value, {
        theme: 'snow',
        placeholder: props.placeholder,
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ['link'],
          ],
          clipboard: {
            matchers: [
              // Блокуємо вставку зображень через clipboard
              [
                'IMG',
                () => {
                  return { ops: [] }
                },
              ],
            ],
          },
          keyboard: {
            bindings: {
              tab: {
                key: 'Tab',
                handler: function () {
                  tabCount++

                  if (tabCount >= 2) {
                    tabCount = 0
                    return false
                  }

                  return true
                },
              },
            },
          },
        },
      })

      await nextTick()

      if (props.modelValue) {
        quillInstance.root.innerHTML = props.modelValue
        // Writing the DOM behind Quill's back leaves the blank flag stale, which would
        // paint the placeholder over a restored draft. Sync before any listener is on.
        quillInstance.update()
      }

      addAccessibilityLabels()
      applyValidationState()

      quillInstance.on('text-change', () => {
        const length = quillInstance!.getLength()
        if (length === 1) {
          emit('isEmptyEditor')
        } else {
          if (!isImagePasting) {
            let text = quillInstance!.getText()
            emit('pureTextForSuggestions', text)
            emit('update:modelValue', turndownService.turndown(quillInstance!.root.innerHTML))
          }
        }
        if (isImagePasting) {
          isImagePasting = false
        }
      })

      quillInstance.root.addEventListener('focus', () => {
        tabCount = 0
      })

      quillInstance.root.addEventListener('blur', () => {
        tabCount = 0
      })

      quillInstance.root.addEventListener(
        'paste',
        (event: ClipboardEvent) => {
          const clipboardData = event.clipboardData
          if (!clipboardData) return

          const items = clipboardData.items
          let processedImagesInThisPaste = 0

          for (let i = 0; i < items.length; i++) {
            const item = items[i]

            if (item.kind === 'file') {
              event.preventDefault()
              event.stopPropagation()
              event.stopImmediatePropagation()
              isImagePasting = true

              // Check if adding this image would exceed the limit
              if (props.attachmentsCount + processedImagesInThisPaste >= MAX_FILES_LIMIT) {
                maxFilesError.value = true
                removeImagesFromEditor()

                setTimeout(() => {
                  removeImagesFromEditor()
                  isImagePasting = false
                }, 10)
                return
              }

              const blob = item.getAsFile()
              if (blob) {
                // Validate file type
                if (!validateFileType(blob.type)) {
                  setValidationErrorWithTimeout()
                  removeImagesFromEditor()
                  setTimeout(() => {
                    removeImagesFromEditor()
                    isImagePasting = false
                  }, 5)
                  return
                }

                const timestamp = new Date().getTime()

                // Map MIME types to proper file extensions
                const mimeToExtension: { [key: string]: string } = {
                  'image/jpeg': 'jpg',
                  'image/jpg': 'jpg',
                  'image/png': 'png',
                  'image/gif': 'gif',
                  'text/plain': 'txt',
                  'application/pdf': 'pdf',
                }

                const extension = mimeToExtension[blob.type] || blob.type.split('/')[1] || 'bin'
                const isImage = blob.type.startsWith('image/')
                const filePrefix = isImage ? 'pasted-image' : 'pasted-file'
                const file = new File([blob], `${filePrefix}-${timestamp}.${extension}`, { type: blob.type })

                maxFilesError.value = false
                clearValidationError()
                emit('pastedImage', file)
                processedImagesInThisPaste++

                // Remove any images that were inserted into the editor
                removeImagesFromEditor()
                // Double-check after a short delay to ensure image is removed
                setTimeout(() => {
                  removeImagesFromEditor()
                  isImagePasting = false
                }, 10)
              }
            }
          }
        },
        true,
      )
    }
  })

  /** Lets the form put the caret back in the message when a submit is refused. */
  const focus = () => quillInstance?.focus()

  defineExpose({ focus })

  const addAccessibilityLabels = () => {
    const toolbar = document.querySelector('.ql-toolbar')
    if (!toolbar) return

    const labelMap = {
      '.ql-header': 'Header',
      '.ql-bold': 'Bold',
      '.ql-italic': 'Italic',
      '.ql-underline': 'Underline',
      '.ql-strike': 'Закреслений',
      '.ql-color': 'Color',
      '.ql-background': 'Background',
      '.ql-align': 'Align',
      '.ql-link': 'Link',
    }

    Object.entries(labelMap).forEach(([selector, label]) => {
      const elements = toolbar.querySelectorAll(selector)
      elements.forEach(element => {
        element.setAttribute('aria-label', label)
        element.setAttribute('title', label)
      })
    })

    const tooltip = document.querySelector('.ql-tooltip')
    if (tooltip) {
      const linkInput = tooltip.querySelector('input[type="text"]')
      if (linkInput) {
        linkInput.setAttribute('aria-label', 'URL посилання')
      }
    }

    const previewLink = tooltip!.querySelector('.ql-preview')
    if (previewLink) {
      previewLink.setAttribute('aria-label', 'Попередній перегляд посилання')
    }
  }
</script>

<template>
  <div>
    <div ref="editor" class="editor-wrapper"></div>
    <div v-if="maxFilesError" class="error-message">
      You cannot attach more than 10 files. Please delete some files before adding new ones.
    </div>
    <div v-if="invalidFileTypeError" class="error-message">
      Allowed file extensions: .jpg, .gif, .jpeg, .png, .txt, .pdf
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .editor-wrapper {
    min-height: 30px;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;

    /* Quill paints its own placeholder, in italic near-black; restate it in the form
       control's placeholder colour so it reads like every other field on the page. */
    :deep(.ql-editor.ql-blank::before) {
      color: var(--bf-form-control-placeholder);
      font-style: normal;
    }

    /* The editable body is a contenteditable div, so the global *:focus rule draws a blue
       box inside the field. The container's focus-within ring is the focus indicator. */
    :deep(.ql-editor:focus) {
      outline: none;
    }
  }

  .error-message {
    margin-top: 8px;
    font-size: 12px;
    font-weight: 400;
    color: var(--color-border-error);
    line-height: 16px;
  }
</style>
